import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Chip,
  Select,
  MenuItem,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CardContent,
  CardHeader,
  Card,
  TableContainer,
  Tooltip,
  Typography,
  Drawer,
} from "@mui/material";

import { productsData, parametersTableData } from "./sampleData";
import Label from "../../../components/Label/Label";
import "./dashboard.scss";
import CAMHeader from "../../../components/Header/CAMHeader";
import CAMDrawer from "../../../components/LeftSideBar/CAMDrawer";
import InvestigatorContainer from "../../../components/contsiners/InvestigatorContainer";
import TabsBar from "../header/TabsNav";
import BreadcrumbsBar from "../header/BreadcrumbNav";
import { useDispatch, useSelector } from "react-redux";
import { fetchGenealogyReports } from "../../../redux/genealogy/actions";
import {
  fetchData,
  fetchProductAndSites,
  genearateKpiTable,
  genearateKpiGrpah,
  setSelectedBatchIds
} from "../../../redux/dashboard/actions";
import BatchFilterPanel from "../visualization/batchFIlters/BatchFilterPanel";
import DashboardBatchFilterPanel from "./DashboardBatchFilterPanel";
import BatchPreview from "./BatchPreview";
import Chart from './Chart'
import MultiChartPopup from "./Chart";
import { formatLocal } from "../../../utility";
import Buttons from "../../../components/Controls/Buttons/Button";

const getStatusStyles = (status) => {
  switch (status) {
    case "ooc":
      return {
        bg: "#F04438", // light red
      };
    case "critical":
      return {
        bg: "#A1B8D2", // dark blue
      };
    default:
      return {
        bg: "#E8EDF4", // light blue
      };
  }
};

export default function Dashboard() {
  const dispatch = useDispatch();
  const productsAndSites = useSelector(
    (state) => state?.dashboard?.productAndSites,
  );

  const response = useSelector(
    (state) => state?.dashboard,
  );
  const [product, setProduct] = useState("");
  const [site, setSite] = useState("");

  const [tableData, setTableData] = useState([]);
  const [graphData, setGraphData] = useState([]);

  const [selectedVariants, setSelectedVariants] = useState([]);
  const [selectedOperations, setSelectedOperations] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // batch preview section

  const [openBatchPanel, setOpenBatchPanel] = useState(false);

  const [batchState, setBatchState] = useState({
    batchRangeOption: "",
    recentBatchCount: "",
    recentDayCount: "",
    startDate: null,
    endDate: null,
    batchIdList: [],
  });

  const [parameterState, setParameterState] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchProductAndSites());
  }, []);



  /* ---------------- LOAD DATA (API SIMULATION) ---------------- */

  // useEffect(() => {
  //   if (product && site) {
  //     const filtered = parametersTableData.filter(
  //       (x) => x.product === product && x.site === site,
  //     );

  //     setTableData(filtered);

  //     setSelectedVariants([]);
  //     setSelectedOperations([]);
  //   }
  // }, [product, site]);

  useEffect(() => {
    if (product && site) {
      dispatch(
        fetchData({
          product,
          site,
        }),
      );

      // reset selections
      setSelectedVariants([]);
      setSelectedOperations([]);
      dispatch(setSelectedBatchIds([]));
    }
  }, [product, site, dispatch]);

  /* ---------------- VARIANTS ---------------- */

  const variants = useMemo(() => {
    return tableData?.length > 0 ? [...new Set(tableData?.map((x) => x.productVariant))] : [];
  }, [tableData]);

  /* ---------------- UNIT OPERATIONS ---------------- */

  const unitOperations = useMemo(() => {
    if (selectedVariants.length === 0) return [];

    return [
      ...new Set(
        tableData?.filter((x) => selectedVariants.includes(x.productVariant))
          .map((x) => x.unitOperation),
      ),
    ];
  }, [tableData, selectedVariants]);

  /* ---------------- FILTERED PARAMETERS ---------------- */

  const filteredParameters = useMemo(() => {
    if (selectedVariants.length === 0 || selectedOperations.length === 0)
      return [];

    return tableData?.filter(
      (x) =>
        selectedVariants.includes(x.productVariant) &&
        selectedOperations.includes(x.unitOperation),
    );
  }, [tableData, selectedVariants, selectedOperations]);

  /* ---------------- GROUP OOC ---------------- */

  const groupedOOC = useMemo(() => {
    const map = {};

    filteredParameters
      .filter((x) => x.group === "OOC")
      .forEach((p) => {
        if (!map[p.phase]) map[p.phase] = [];
        map[p.phase].push(p);
      });

    return map;
  }, [filteredParameters]);

  /* ---------------- GROUP PROCESS CAPABILITY ---------------- */

  const groupedPC = useMemo(() => {
    const map = {};

    filteredParameters
      .filter((x) => x.group === "PROCESS_CAPABILITY")
      .forEach((p) => {
        if (!map[p.phase]) map[p.phase] = [];
        map[p.phase].push(p);
      });

    return map;
  }, [filteredParameters]);

  /* ---------------- TOGGLE FUNCTION ---------------- */

  const toggleSelection = (value, list, setter) => {
    if (list.includes(value)) {
      setter(list.filter((x) => x !== value));
    } else {
      setter([...list, value]);
    }
  };

  // TEMP PREFILL (REMOVE LATER)
  useEffect(() => {
    // setProduct("Xywav");
    // setSite("Jazz Athlone");
    dispatch(setSelectedBatchIds([]));
  }, []);

  // TEMP PREFILL (REMOVE LATER)
  useEffect(() => {
    if (variants.length > 0) {
      setSelectedVariants(variants);
    }
  }, [variants]);

  // TEMP PREFILL (REMOVE LATER)
  useEffect(() => {
    if (unitOperations.length > 0) {
      setSelectedOperations(unitOperations);
    }
  }, [unitOperations]);

  const updateBatchState = (key, value) => {
    setBatchState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePreview = () => {
    setShowPreview(true);

    // call API here if needed
  };

  const handleGenerate = () => {

    // final API call
  };

  const handleClearAll = () => {
    setBatchState({
      batchRangeOption: "",
      recentBatchCount: "",
      recentDayCount: "",
      startDate: null,
      endDate: null,
      batchIdList: [],
    });

    setPreviewData([]);
    setShowPreview(false);
  };

  const onClickApply = async (ids) => {
    setSelectedIds(ids);
    const payload = {
      main_product_name: product,
      site_variant_id: site,
      batchs_selection: {
        batch_id_list: ids,
        batch_range_option: batchState.batchRangeOption,
        recent_batch_count: batchState.recentBatchCount,
        recent_day_count: batchState.recentDayCount,
        start_date: formatLocal(batchState.startDate),
        end_date: formatLocal(batchState.endDate),
      }

    };

    try {
      const res = await dispatch(genearateKpiTable(payload));
      setShowPreview(false);
      setTableData(res?.data?.result || []);

    } catch (error) {
      setTableData([]);
    }
  }

  const onView = async (data) => {
    setChartOpen(true)
    const payload = {
      main_product_id: product,
      main_product_name: data.main_product_name,
      main_site_id: site,
      site_variant_id: [site],
      product_varient_id: [data.product_varient_id],
      unit_op_id: [data.unit_op_id],
      parameter_id: [data.parameter_id],
      batchs_selection: {
        batch_id_list: selectedIds,
        batch_range_option: batchState.batchRangeOption,
        recent_batch_count: batchState.recentBatchCount,
        recent_day_count: batchState.recentDayCount,
        start_date: formatLocal(batchState.startDate),
        end_date: formatLocal(batchState.endDate),
      }

    };



    try {
      const res = await dispatch(genearateKpiGrpah(payload));
      setShowPreview(false);
      setGraphData(res?.data || []);

    } catch (error) { }
  }

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedUnitOp, setSelectedUnitOp] = useState(null);

  const filteredByVariantAndUnitOp = useMemo(() => {
    if (!selectedVariant || !selectedUnitOp) return [];
    return tableData.filter(
      (x) =>
        x.product_varient_name === selectedVariant &&
        x.unit_op_name === selectedUnitOp
    );
  }, [tableData, selectedVariant, selectedUnitOp]);

  // Group data by variant → unit operation
  const groupedByVariant = useMemo(() => {
    if (!tableData || tableData.length === 0) return {};

    return tableData.reduce((acc, curr) => {
      const variantName = curr.product_varient_name;
      const unitOpName = curr.unit_op_name;

      if (!acc[variantName]) acc[variantName] = {};
      if (!acc[variantName][unitOpName]) acc[variantName][unitOpName] = [];
      acc[variantName][unitOpName].push(curr);

      return acc;
    }, {});
  }, [tableData]); // dependency fixed

  const groupByPhase = (data, key, type) => {
    const map = {};

    data
      .filter((x) => {
        const flag = x[key];

        // ❌ remove NA flags
        if (!flag || flag === "NA") return false;

        // ❌ remove green only for PPK
        if (flag === "green") return false;

        return true;
      })
      .forEach((p) => {
        let phase = p.Phase;

        // ✅ treat NA as no phase
        if (!phase || phase === "NA") {
          phase = "";
        }

        if (!map[phase]) map[phase] = [];
        map[phase].push(p);
      });

    return map;
  };

  const groupedOOCSelected = useMemo(
    () => groupByPhase(filteredByVariantAndUnitOp, "ooc_flag", "ooc"),
    [filteredByVariantAndUnitOp]
  );

  const groupedPCSelected = useMemo(
    () => groupByPhase(filteredByVariantAndUnitOp, "Ppk_flags", "ppk"),
    [filteredByVariantAndUnitOp]
  );

  {/* REUSABLE PARAMETER SECTION */ }

  const oocColorMap = {
    red: {
      bg: "#FEE4E2",
      text: "#F04438",
    },
    blue: {
      bg: "#E0F2FE",   // softer, cleaner light blue
      text: "#1D4ED8", // deeper blue (better contrast & less neon)
    },
    grey: {
      bg: "#E4E7EC",
      text: "#475467",
    },
    white: {
      bg: "#FFFFFF",
      text: "#000000",
    },

    yellow: {
      bg: "#FFF7ED",   // very light warm tone
      text: "#C2410C", // strong orange (not dull yellow)
    },

    // 🟣 UNIQUE mid-state (no confusion at all)
    yellowgreen: {
      bg: "#F3E8FF",   // light purple
      text: "#7E22CE", // strong purple
    },

    // 🟢 Good (kept green but stronger separation)
    green: {
      bg: "#DCFCE7",
      text: "#11971c",
    },
  };

  const legendOoc = [
    { key: "red", label: ">= 5%" },
    { key: "blue", label: "2% - 5%" },
    { key: "grey", label: "1% - 2%" },
    // { key: "white", label: "0%" },
  ];
  const legendppk = [
    { key: "red", label: " < 0.68" },
    // { key: "blue", label: "1 - 1.33" },
    // { key: "green", label: "1.33" },
    { key: "blue", label: "0.68 - 1" },
    // { key: "green", label: ">= 1 " },
    // { key: "white", label: "0%" },
  ];


  const getUnitOpRedCount = (variant, unitOp) => {
    const params = groupedByVariant?.[variant]?.[unitOp] || [];

    return params.filter(
      (p) => p.ooc_flag === "red" || p.Ppk_flags === "red"
    ).length;
  };

  const ParameterSection = ({ title, groupedData, legend, type }) => {
    // Map OOC flags to colors


    return (
      <Paper sx={{ mt: 1, borderRadius: "8px", p: 1 }}>
        <Box>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ "& .label": { color: "#154D8F !important" } }}>
              <Label text={title} fontWeight="500" />
              {/* Right: Legend */}

            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {legend.map((item) => {
                const color = oocColorMap[item.key];
                return (
                  <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {/* Color Circle */}
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: color?.text || "transparent",
                      }}
                    />
                    {/* Label Text */}
                    <Typography sx={{ fontSize: "12px", color: color?.text, fontWeight: 500 }}>
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: colorCode,
              }}
            />
            <Typography>{colorText}</Typography>
          </Box> */}
          {/* Empty State */}
          {!groupedData || Object.keys(groupedData).length === 0 ? (
            <Typography sx={{ mt: 2, color: "#555" }}>No parameters</Typography>
          ) : (
            Object.keys(groupedData || []).map((phase) => (
              <Card
                key={phase}
                elevation={1}
                sx={{
                  mt: 1,
                  borderRadius: "4px",
                  overflow: "hidden",
                  border: "1px solid #d9d9d9",
                  boxShadow: "none",
                }}
              >
                {/* Phase Header */}
                {phase && <CardHeader
                  title={<Label text={phase} fontSize="12px" fontWeight="600" />}
                  sx={{
                    backgroundColor: "#E9EAEB",
                    padding: "6px 12px",
                    "& .MuiCardHeader-content": { margin: 0 },
                  }}
                />}

                {/* Parameters */}
                <CardContent sx={{ p: "8px !important" }}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {groupedData[phase].map((p) => {
                      // Get OOC colors if present
                      const flag = type === "ooc" ? p.ooc_flag : p.Ppk_flags;
                      const color = oocColorMap[flag];

                      return (
                        <Chip
                          key={p.parameter_id || p.id}
                          label={p["Parameter name"] || p.parameter || "Unnamed"}
                          size="small"
                          sx={{
                            borderRadius: "4px",
                            height: "26px",
                            fontSize: "12px",
                            backgroundColor: color?.bg || 'transparent',
                            color: color?.text || 'black',
                            "& .MuiChip-label": {
                              fontWeight: "500 !important",
                              px: 1,
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      </Paper>
    );
  };


  return (
    <div>
      <CAMHeader />
      <CAMDrawer />
      <InvestigatorContainer>
        {/* <DropdownsBar /> */}
        <Grid container alignItems="center" spacing={2}>
          {/* Tabs */}
          <Grid item xs={12} md={12} sx={{ pr: { md: 0 } }}>
            <TabsBar />
          </Grid>
        </Grid>

        <Divider sx={{ mx: 2 }} />
        <Grid item xs={12} md={12} sx={{ pr: { md: 0 } }}>
          <BreadcrumbsBar />
        </Grid>
        <Divider sx={{ mx: 2 }} />

        <Grid
          container
          spacing={2}
          sx={{
            p: 1,
            height: "calc(100vh - 115px)", // adjust based on header
          }}
        >
          {/* LEFT PANEL */}

          <Grid item xs={6} sx={{ height: "100%", overflow: "hidden" }}>
            {" "}
            <Paper
              sx={{
                p: 1,
                backgroundColor: "#E8EDF4",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* FILTERS */}
              <Box
                sx={{
                  overflowY: "auto",
                  flex: 1,
                  pr: 1, // optional spacing for scrollbar
                }}
              >
                <Grid
                  container
                  spacing={1}
                  sx={{
                    padding: "8px",
                    "& .label": { color: "#fff !important" },
                  }}
                >
                  {/* Product + Site Section */}
                  <Grid
                    item
                    xs={8}
                    sx={{
                      backgroundColor: "#0D2E56",
                      padding: "8px",
                      display: "flex",
                      gap: 1,
                      borderRadius: "8px",
                    }}
                  >
                    <Grid item xs={6}>
                      <Label
                        text="Product"
                        style={{ color: "#fff", fontWeight: 500 }}
                      />

                      <Select
                        size="small"
                        fullWidth
                        value={product}
                        onChange={(e) => setProduct(e.target.value)}
                        sx={{
                          backgroundColor: "#12437C",
                          "& .MuiSvgIcon-root": { color: "#fff" },
                        }}
                      >
                        {productsAndSites?.data?.length > 0 && productsAndSites?.data?.map((p) => (
                          <MenuItem key={p.main_product_id} value={p.main_product_name}>
                            <Label text={p.main_product_name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    <Grid item xs={6}>
                      <Label text="Site" />

                      <Select
                        size="small"
                        fullWidth
                        value={site}
                        onChange={(e) => setSite(e.target.value)}
                        sx={{
                          backgroundColor: "#12437C",
                          "& .MuiSvgIcon-root": { color: "#fff" },
                        }}
                      >
                        {(
                          productsAndSites?.data?.length > 0 && productsAndSites?.data?.find((p) => p.main_product_name === product)
                            ?.sites || []
                        ).map((s) => (
                          <MenuItem key={s.siteid} value={s.siteid}>
                            <Label text={s.sitename} />
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>

                  {/* Batch Filter Section */}
                  <Grid
                    item
                    xs={3.7}
                    sx={{
                      backgroundColor: "#0D2E56",
                      padding: "8px",
                      marginLeft: "8px",
                      borderRadius: "8px",
                    }}
                  >
                    <Label text="Batch Filter" />

                    <Buttons
                      variant="contained"
                      fullWidth
                      onClick={() => setShowPreview(true)}
                      sx={{
                        backgroundColor: "#12437C",
                        color: "#fff",
                        textTransform: "none",
                        justifyContent: "flex-start",
                        "&:hover": {
                          backgroundColor: "#0f3a6a",
                        },
                      }}
                      text="Select Batches"
                    >

                    </Buttons>
                  </Grid>
                </Grid>

                <Box sx={{ p: 2, backgroundColor: '#D0DBE9', p: 1, borderRadius: '8px' }}>
                  {/* Product Variant Chips */}
                  <Typography variant="subtitle1">Product Variant</Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                    {Object.keys(groupedByVariant).map((variant) => {
                      const isSelected = selectedVariant === variant;

                      // Count all parameters under this variant, excluding ooc_flag and Ppk_flag
                      let paramCount = 0;
                      const units = groupedByVariant[variant];

                      Object.keys(units).forEach((unitOp) => {
                        const params = units[unitOp] || [];

                        paramCount += params.filter(
                          (p) => p.ooc_flag === "red" || p.Ppk_flags === "red"
                        ).length;
                      });

                      return (
                        <Box
                          key={variant}
                          onClick={() => {
                            setSelectedVariant(variant);
                            setSelectedUnitOp(null);
                          }}
                          sx={{
                            cursor: "pointer",
                            borderRadius: "4px",
                            border: isSelected ? "1px solid #154D8F" : "1px solid #D0D5DD",
                            backgroundColor: isSelected ? "#154D8F" : "#FFFFFF",
                            color: isSelected ? "#fff" : "#000",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "max-content", // slightly wider for name
                            height: "80px",
                            p: 1,
                            "&:hover": {
                              backgroundColor: isSelected ? "#123E73" : "#F2F4F7",
                            },
                          }}
                        >
                          {/* Count in a small box */}
                          <Box sx={{ display: "flex", alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 500,
                                textAlign: "center",
                                color: isSelected ? "#fff" : "#000",
                                mb: 0.25,
                              }}
                            >
                              {variant}
                            </Typography>
                            <Box sx={{
                              display: "flex",
                              flexDirection: 'column',
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: '#103A6D',
                              p: 1,
                              borderRadius: '4px'
                            }}>
                              <Box
                                sx={{

                                  color: 'white',
                                  width: "28px",
                                  height: "28px",
                                  borderRadius: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 600,
                                  mb: 0.5,
                                }}
                              >
                                {paramCount}

                              </Box>
                              <Typography
                                sx={{
                                  fontSize: "10px",
                                  textAlign: "center",
                                  color: '#fff',
                                }}
                              >
                                Alarm
                              </Typography>
                            </Box>
                          </Box>
                          {/* Variant Name */}


                          {/* Alarm Text */}

                        </Box>
                      );
                    })}
                  </Box>
                  {/* Unit Operation Chips */}
                  {selectedVariant && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1">Unit Operations</Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                        {Object.keys(groupedByVariant[selectedVariant] || {}).map((unitOp) => {
                          const isSelected = selectedUnitOp === unitOp;
                          return (
                            <Chip
                              key={unitOp}
                              label={`${unitOp} - (${getUnitOpRedCount(selectedVariant, unitOp)})`}
                              clickable
                              onClick={() => setSelectedUnitOp(unitOp)}
                              sx={{
                                borderRadius: "4px",
                                height: "36px",
                                fontWeight: 500,
                                fontSize: '14px',
                                backgroundColor: isSelected ? "#154D8F" : "#FFFFFF",
                                color: isSelected ? "#fff" : "#000",
                                border: isSelected ? "1px solid #154D8F" : "1px solid #D0D5DD",
                                "&:hover": {
                                  backgroundColor: isSelected ? "#123E73" : "#F2F4F7",
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    </>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <ParameterSection title="OOC Parameters" groupedData={groupedOOCSelected} legend={legendOoc} type='ooc' />
                  <ParameterSection title="Process Capability" groupedData={groupedPCSelected} legend={legendppk} type='ppk' />
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT TABLE */}

          <Grid item xs={6} sx={{ height: "100%", overflow: "hidden" }}>
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: "8px",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: 600,
                  backgroundColor: "#E8EDF4",
                  padding: "8px",
                  marginBottom: "8px",
                  borderRadius: "8px",
                }}
              >
                <Label text="Overview of Parameters" />
              </Box>

              {/* Table Wrapper for Scroll */}
              <TableContainer
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  // pt: 1,
                }}
              >
                <Table size="small" stickyHeader>
                  {/* ================= TABLE HEAD ================= */}
                  <TableHead>
                    <TableRow>
                      {[
                        'Sr No.',
                        "Product",
                        "Unit Operation",
                        "Table Name",
                        "Parameter",
                        "OOC",
                        "Process Capability",
                        'Total Points',
                        "OOC Count",
                        "% OOC",
                        'Total Batch',
                        "Cpk",
                        "Ppk",
                        '% Above USL',
                        '% Below LSL',
                        'Total % Risk'
                      ].map((header, index, arr) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            backgroundColor: "#E9E9E9",
                            borderRadius: 'none',

                            ...(index === 0 && {
                              borderTopLeftRadius: "8px",
                            }),
                            ...(index === arr.length - 1 && {
                              borderTopRightRadius: "8px",
                            }),
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  {/* ================= TABLE BODY ================= */}
                  <TableBody>
                    {tableData?.map((row, index) => {
                      const styles = getStatusStyles(row.status);

                      return (
                        <TableRow key={row.id}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{row.product_varient_name}</TableCell>

                          <TableCell>{row.unit_op_name}</TableCell>
                          <TableCell>{row.table_level}</TableCell>
                          <TableCell>
                            <Typography
                              color="primary"
                              sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                              onClick={() => onView(row)}
                            >
                              {row['Parameter name']}
                            </Typography>
                          </TableCell>
                          {/* <TableCell>{row['Parameter name']}</TableCell> */}
                          <TableCell>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor: row.ooc_flag === 'white' ? 'white' : oocColorMap[row.ooc_flag]?.text || 'white', // use your config
                                margin: "0 auto", // center the dot in cell
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor: row.Ppk_flags === 'white' ? 'white' : oocColorMap[row.Ppk_flags]?.text || 'white', // use your config
                                margin: "0 auto", // center the dot in cell
                              }}
                            />
                          </TableCell>
                          <TableCell>{row['Total points']}</TableCell>
                          <TableCell>{row['OOC count']}</TableCell>
                          <TableCell>{row['OOC percent']}</TableCell>
                          <TableCell>{row['Total batches']}</TableCell>
                          <TableCell>{row['Cpk']}</TableCell>
                          <TableCell>{row['Ppk']}</TableCell>
                          <TableCell>{row['Percent Above USL']}</TableCell>
                          <TableCell>{row['Percent Below LSL']}</TableCell>
                          <TableCell>{row['Total Percent Risk']}</TableCell>

                          {/* ✅ OOC Indicator */}
                          {/* <TableCell>
                            <Tooltip title={row.status?.toUpperCase()}>
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  bgcolor: styles.bg,
                                  // border: `1px solid ${styles.border}`,
                                }}
                              />
                            </Tooltip>
                          </TableCell> */}

                          {/* % OOC */}
                          {/* <TableCell>{row.percent}</TableCell> */}
                        </TableRow>
                      );
                    })}
                    {!tableData.loader && tableData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={16} align="center">
                          <Label color="textSecondary" text={"No data found"} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </InvestigatorContainer>
      <MultiChartPopup
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        control_chart={graphData?.control_chart}
        pcc_chart={graphData?.pcc_chart}
        isLoading={response}
      />
      {/* <Drawer
        anchor="right"
        open={openBatchPanel}
        onClose={() => setOpenBatchPanel(false)}
        PaperProps={{
          sx: { width: 420 },
        }}
      >
        <DashboardBatchFilterPanel
          productId={product}
          siteId={site}
          batchState={batchState}
          updateState={updateBatchState}
          setPreviewData={setPreviewData}
          setShowPreview={setShowPreview}
          onClose={() => setOpenBatchPanel(false)}
        />
      </Drawer> */}

      {showPreview && (
        <Grid item xs={4} className=" right-drawer">
          <BatchPreview
            showPreview={showPreview}
            setShowPreview={setShowPreview}
            previewData={previewData}
            setPreviewData={setPreviewData}
            onClickApply={onClickApply}
            productId={product}
            siteId={site}
            batchState={batchState}
            updateState={updateBatchState}
            onClose={() => setShowPreview(false)}
            kpitableres={response}
          // disableAllFields={disableAllFields}
          // batchLoader={batchLoader}
          />
        </Grid>
      )}
    </div>
  );
}
